import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import BusinessIcon from '@mui/icons-material/Business';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DataTable from '../../../sharedModules/dataTable';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { useCollectionsLocation } from '../../shared/useCollectionsLocation';
import { collectionCandidatesQueryKey, useCollectionClients } from '../hooks/useCollectionClients';
import { useUpdateCollectionClient } from '../hooks/useUpdateCollectionClient';
import { CollectionClient, CollectionInvoiceDocument, CollectionInvoicePdf } from '../model';
import InvoicePdfsModal from '../components/InvoicePdfsModal';
import CollectionClientNotesModal from '../components/CollectionClientNotesModal';
import {
  CollectionClientsTableTooltip,
  collectionClientsTooltipProps,
  NO_BALANCE_OWED_TOGGLE_TOOLTIP,
  bucketDisabledToggleTooltip,
  RESIDENTIAL_SUPPRESSED_TOGGLE_TOOLTIP,
  NO_USABLE_EMAIL_TOGGLE_TOOLTIP,
} from '../components/CollectionClientsTableTooltip';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { buildDepartmentGroupOptions } from '../../shared/departmentGroup/departmentGroup';
import { useDepartmentGroupDefault } from '../../shared/departmentGroup/useDepartmentGroupDefault';
import DepartmentGroupSelect from '../../shared/departmentGroup/DepartmentGroupSelect';
import {
  formatDollar,
  permanentOffTooltip,
  ADMIN_ONLY_TOGGLE_TOOLTIP,
  ADMIN_ONLY_PERMANENT_TOOLTIP,
} from '../utils/format';
import {
  scopeClient,
  DEFAULT_PAST_DUE_THRESHOLD_DAYS,
  DEFAULT_FRIENDLY_THRESHOLD_DAYS,
  DEFAULT_SECOND_NOTICE_THRESHOLD_DAYS,
} from '../utils/scopeClientToDepartment';
import { useCollectionConfig } from '../../config/hooks/useCollectionConfig';
import CollectionClientsAgingSummary, { AgingBucket } from '../components/CollectionClientsAgingSummary';
import { useUpdateCollectionClientPermanentStatus } from '../hooks/useUpdateCollectionClientPermanentStatus';
import PermanentOffReasonModal from '../components/PermanentOffReasonModal';

interface InvoicesModalState {
  open: boolean;
  company: string;
  documents: CollectionInvoiceDocument[];
  acctNo: string;
}

interface NotesModalState {
  open: boolean;
  clientId: number;
  company: string;
}

type TriState = 'all' | 'yes' | 'no';

const matchedPdfCount = (documents: CollectionInvoiceDocument[]): number => documents.filter((d) => d.has_pdf).length;

/** Required on cell content for DataTable row clicks (see sharedModules/dataTable/DataTable/TableRow). */
const ROW_CLICK_TAG = '___react-data-table-allow-propagation___';

type DollarColorMode = 'debit' | 'credit' | 'pastDue';

const getDollarColor = (amount: number, mode: DollarColorMode): string => {
  switch (mode) {
    case 'debit':
      return amount > 0 ? 'error.main' : 'text.primary';
    case 'credit':
      return amount < 0 ? 'info.main' : 'text.primary';
    case 'pastDue':
      if (amount > 0) return 'error.main';
      if (amount < 0) return 'info.main';
      return 'text.primary';
    default:
      return 'text.primary';
  }
};

const DollarAmountCell: React.FC<{ amount: number; mode: DollarColorMode }> = ({ amount, mode }) => (
  <Typography variant="body2" sx={{ color: getDollarColor(amount, mode) }} data-tag={ROW_CLICK_TAG}>
    {formatDollar(amount)}
  </Typography>
);

const buildColumns = (
  onInvoicesClick: (company: string, documents: CollectionInvoiceDocument[], acctNo: string) => void,
  onToggleClient: (row: CollectionClient, checked: boolean) => void,
  isToggling: boolean,
  onNotesClick: (row: CollectionClient) => void,
  onViewClick: (row: CollectionClient) => void,
  isCollectionsAdmin: boolean,
  onPermanentToggle: (row: CollectionClient, checked: boolean) => void,
  isPermanentSaving: boolean,
) => [
  {
    name: 'Company',
    selector: (row: CollectionClient) => row.company,
    sortable: true,
    grow: 2,
    minWidth: '360px',
    cell: (row: CollectionClient) => {
      const permanentlyOff = row.collection_permanently_disabled;
      const blocked = !row.toggle_allowed;
      const toggleDisabled = isToggling || blocked || !isCollectionsAdmin || permanentlyOff;
      const showTooltip = blocked || permanentlyOff || !isCollectionsAdmin;

      let tooltipText = NO_USABLE_EMAIL_TOGGLE_TOOLTIP;
      if (permanentlyOff) {
        tooltipText = permanentOffTooltip(
          row.collection_permanently_disabled_by,
          row.collection_permanently_disabled_at,
          row.collection_permanently_disabled_reason,
        );
      } else if (row.suppression_reason === 'no_balance_owed') tooltipText = NO_BALANCE_OWED_TOGGLE_TOOLTIP;
      else if (row.suppression_reason === 'residential') tooltipText = RESIDENTIAL_SUPPRESSED_TOGGLE_TOOLTIP;
      // bucket_disabled reflects the overall tone, not the department-scoped tone on the row.
      else if (row.suppression_reason === 'bucket_disabled')
        tooltipText = bucketDisabledToggleTooltip(row.overall_tone ?? row.tone);
      else if (!isCollectionsAdmin) tooltipText = ADMIN_ONLY_TOGGLE_TOOLTIP;

      const isActive = blocked || permanentlyOff ? false : row.collection_enabled;

      const switchControl = (
        <Switch
          checked={isActive}
          onChange={(event) => onToggleClient(row, event.target.checked)}
          onClick={(e) => e.stopPropagation()}
          color="primary"
          size="small"
          disabled={toggleDisabled}
          inputProps={{
            'aria-label': `Collection emails for ${row.company}`,
            'aria-disabled': toggleDisabled,
          }}
        />
      );

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          {showTooltip ? (
            <span {...collectionClientsTooltipProps(tooltipText)} style={{ display: 'inline-flex' }}>
              {switchControl}
            </span>
          ) : (
            switchControl
          )}
          <Typography
            variant="body2"
            data-tag={ROW_CLICK_TAG}
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {row.company}
          </Typography>
        </Box>
      );
    },
  },
  { name: 'Bill Code', selector: (row: CollectionClient) => row.bill_code, sortable: true, width: '110px' },
  {
    name: 'Emailable',
    selector: (row: CollectionClient) => row.emailable_status,
    sortable: true,
    cell: (row: CollectionClient) => {
      if (row.emailable_status === 'no_email') {
        return <Chip label="No email" color="error" size="small" data-tag={ROW_CLICK_TAG} />;
      }
      return <Chip label="Yes" color="success" size="small" data-tag={ROW_CLICK_TAG} />;
    },
    width: '80px',
  },
  {
    name: 'Total Debit',
    selector: (row: CollectionClient) => row.total_debit,
    cell: (row: CollectionClient) => <DollarAmountCell amount={row.total_debit} mode="debit" />,
    sortable: true,
    width: '120px',
  },
  {
    name: 'Total Credit',
    selector: (row: CollectionClient) => row.total_credit,
    cell: (row: CollectionClient) => <DollarAmountCell amount={row.total_credit} mode="credit" />,
    sortable: true,
    width: '120px',
  },
  {
    name: 'Total Past Due',
    selector: 'total_past_due',
    cell: (row: CollectionClient) => <DollarAmountCell amount={row.total_past_due} mode="pastDue" />,
    sortable: true,
    width: '140px',
  },
  {
    name: 'Worst Aging Days',
    selector: (row: CollectionClient) => row.worst_aging_days,
    sortable: true,
    width: '135px',
  },
  {
    name: 'Invoices',
    // Sort by missing files (total - matched), so descending puts clients with the most missing invoice PDFs on top.
    selector: (row: CollectionClient) => row.invoice_count - matchedPdfCount(row.invoice_documents),
    sortable: true,
    center: true,
    cell: (row: CollectionClient) => {
      if (row.invoice_count === 0) {
        return (
          <Typography variant="body2" data-tag={ROW_CLICK_TAG}>
            0
          </Typography>
        );
      }
      const total = row.invoice_count;
      const matched = matchedPdfCount(row.invoice_documents);
      const allMatched = matched === total;
      return (
        <Chip
          icon={<PictureAsPdfIcon />}
          label={`${matched} / ${total}`}
          size="small"
          color={allMatched ? 'primary' : 'error'}
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            onInvoicesClick(row.company, row.invoice_documents, row.acct_no);
          }}
          sx={{ cursor: 'pointer', px: 1, py: 0.5, height: 'auto' }}
          aria-label={
            allMatched ? `All ${total} invoices have PDFs` : `${matched} of ${total} invoices have PDFs (missing some)`
          }
        />
      );
    },
    width: '90px',
  },
  {
    name: 'Notes',
    selector: (row: CollectionClient) => row.notes_count,
    sortable: true,
    center: true,
    cell: (row: CollectionClient) => (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onNotesClick(row);
        }}
        title="View notes"
        aria-label={`${row.notes_count} notes for ${row.company}`}
      >
        <Badge
          badgeContent={row.notes_count}
          color="primary"
          max={99}
          showZero
          sx={{
            '& .MuiBadge-badge': {
              minWidth: 22,
              height: 20,
              padding: '0 6px',
              fontSize: '0.75rem',
              lineHeight: '20px',
              whiteSpace: 'nowrap',
            },
          }}
        >
          <ChatBubbleOutlineIcon fontSize="small" />
        </Badge>
      </IconButton>
    ),
    width: '100px',
  },
  {
    name: 'Permanent Off',
    center: true,
    width: '40px',
    cell: (row: CollectionClient) => {
      const permanentlyOff = row.collection_permanently_disabled;
      const disabled = !isCollectionsAdmin || !row.collections_client_id || isPermanentSaving;
      let tip = ADMIN_ONLY_PERMANENT_TOOLTIP;
      if (permanentlyOff) {
        tip = permanentOffTooltip(
          row.collection_permanently_disabled_by,
          row.collection_permanently_disabled_at,
          row.collection_permanently_disabled_reason,
        );
      }
      const showTip = permanentlyOff || !isCollectionsAdmin;
      const control = (
        <Switch
          checked={permanentlyOff}
          onChange={(e) => onPermanentToggle(row, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          color="error"
          size="small"
          disabled={disabled}
          inputProps={{ 'aria-label': `Permanently disable collections for ${row.company}` }}
          sx={{
            '& .MuiSwitch-switchBase:not(.Mui-checked)': {
              color: 'primary.main',
            },
            '& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track': {
              backgroundColor: 'primary.main',
              opacity: 0.5,
            },
          }}
        />
      );
      return showTip ? (
        <span {...collectionClientsTooltipProps(tip)} style={{ display: 'inline-flex' }} data-tag={ROW_CLICK_TAG}>
          {control}
        </span>
      ) : (
        control
      );
    },
  },
  {
    name: '',
    width: '50px',
    center: true,
    cell: (row: CollectionClient) => (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onViewClick(row);
        }}
        title="View client details"
        aria-label={`View details for ${row.company}`}
        disabled={!row.collections_client_id}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
    ),
  },
];

const filterRows = (
  rows: CollectionClient[],
  customer: string,
  emailable: TriState,
  collectionEnabled: TriState,
  billCode: string,
): CollectionClient[] => {
  const qCustomer = customer.trim().toLowerCase();
  const qBillCode = billCode.trim().toLowerCase();

  return rows.filter((row) => {
    if (qCustomer && !row.company.toLowerCase().includes(qCustomer)) return false;
    if (emailable !== 'all') {
      const wantYes = emailable === 'yes';
      if (row.emailable !== wantYes) return false;
    }
    if (collectionEnabled !== 'all') {
      const wantYes = collectionEnabled === 'yes';
      if (row.collection_enabled !== wantYes) return false;
    }
    if (qBillCode && !row.bill_code.toLowerCase().includes(qBillCode)) return false;
    return true;
  });
};

const CollectionClientsListPage: React.FC = () => {
  const history = useHistory();
  const location = useCollectionsLocation();
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useCollectionClients(location ?? '', !!location);
  const { updateClient, isLoading: isToggling } = useUpdateCollectionClient();
  const { updatePermanentStatus, isLoading: isPermanentSaving } = useUpdateCollectionClientPermanentStatus();
  const { data: configData, isLoading: isConfigLoading } = useCollectionConfig(location ?? '', !!location);
  const config = configData?.data;
  const tablePending = isLoading || isConfigLoading;
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);
  // isCollectionsAdmin comes from useDepartmentGroupDefault (which already wraps useCollectionsPermissions)
  // rather than calling useCollectionsPermissions a second time here.
  const { user, myGroups, defaultDepartmentGroup, myGroupsLabel, isCollectionsAdmin } = useDepartmentGroupDefault();
  const [clients, setClients] = useState<CollectionClient[]>([]);
  const [permanentModal, setPermanentModal] = useState<{ open: boolean; client: CollectionClient | null }>({
    open: false,
    client: null,
  });

  const [customerFilter, setCustomerFilter] = useState('');
  const [emailableFilter, setEmailableFilter] = useState<TriState>('all');
  const [collectionEnabledFilter, setCollectionEnabledFilter] = useState<TriState>('all');
  const [billCodeFilter, setBillCodeFilter] = useState('');
  const [departmentGroupFilter, setDepartmentGroupFilter] = useState<string>('all');
  const [selectedBucket, setSelectedBucket] = useState<AgingBucket | null>(null);

  const [invoicesModal, setInvoicesModal] = useState<InvoicesModalState>({
    open: false,
    company: '',
    documents: [],
    acctNo: '',
  });

  const [notesModal, setNotesModal] = useState<NotesModalState>({
    open: false,
    clientId: 0,
    company: '',
  });

  useEffect(() => {
    if (data?.data) {
      setClients(data.data);
    }
  }, [data]);

  // Apply each user's default group filter once when their session resolves. Keyed on user id
  // (not a boolean) so an impersonation / user switch while mounted re-applies the new default,
  // while still never clobbering a manual filter change within the same user's session.
  const appliedForUserRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user?.id || appliedForUserRef.current === user.id) return;
    appliedForUserRef.current = user.id;
    setDepartmentGroupFilter(defaultDepartmentGroup);
  }, [user, defaultDepartmentGroup]);

  // "No department" is intentionally not offered as a filter here: an ungrouped bucket would net
  // credit lines in a way that's hard to reconcile with the per-department scoped totals, so we
  // suppress that option on this page (hasUngrouped forced false). Other filters are unaffected.
  const departmentGroupOptions = useMemo(
    () => ({ ...buildDepartmentGroupOptions(clients, myGroups), hasUngrouped: false }),
    [clients, myGroups],
  );

  // Scope each client's totals/tone/aging to the selected department by re-aggregating only its
  // matching aging lines. 'all' keeps backend totals verbatim (penny-accurate, no float drift).
  const scopedClients = useMemo(() => {
    if (departmentGroupFilter === 'all') return clients;
    const thresholds = {
      pastDueThresholdDays: config?.past_due_threshold_days ?? DEFAULT_PAST_DUE_THRESHOLD_DAYS,
      friendlyThresholdDays: config?.friendly_threshold_days ?? DEFAULT_FRIENDLY_THRESHOLD_DAYS,
      secondNoticeThresholdDays: config?.second_notice_threshold_days ?? DEFAULT_SECOND_NOTICE_THRESHOLD_DAYS,
    };
    return clients
      .map((client) => scopeClient(client, departmentGroupFilter, myGroups, thresholds))
      .filter((client): client is CollectionClient => client !== null);
  }, [clients, departmentGroupFilter, myGroups, config]);

  const clientsForSummary = useMemo(
    () => filterRows(scopedClients, customerFilter, emailableFilter, collectionEnabledFilter, billCodeFilter),
    [scopedClients, customerFilter, emailableFilter, collectionEnabledFilter, billCodeFilter],
  );

  const filteredClients = useMemo(
    () => (selectedBucket ? clientsForSummary.filter((row) => row.tone === selectedBucket) : clientsForSummary),
    [clientsForSummary, selectedBucket],
  );

  const summaryLoading = isLoading || isConfigLoading;

  if (!location) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <PageHeader mainTitle="Collections — Clients" titleIcon={<BusinessIcon />} />
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Alert severity="info">No Collections location is assigned to this account.</Alert>
        </Grid>
      </Grid>
    );
  }

  const handleClearFilters = () => {
    setCustomerFilter('');
    setEmailableFilter('all');
    setCollectionEnabledFilter('all');
    setBillCodeFilter('');
    setDepartmentGroupFilter(defaultDepartmentGroup);
    setSelectedBucket(null);
  };

  const handleToggleClient = async (client: CollectionClient, collectionEnabled: boolean) => {
    if (!client.collections_client_id) {
      showErrorSnackBar?.('Client not available for update.');
      return;
    }

    const previousCollectionEnabled = client.collection_enabled;
    setClients((prev) =>
      prev.map((row) =>
        row.collections_client_id === client.collections_client_id
          ? { ...row, collection_enabled: collectionEnabled }
          : row,
      ),
    );

    try {
      await updateClient({ id: client.collections_client_id, collectionEnabled });
      showSuccessSnackBar?.(`Client ${collectionEnabled ? 'enabled' : 'disabled'} for collection emails.`);
    } catch (error: any) {
      setClients((prev) =>
        prev.map((row) =>
          row.collections_client_id === client.collections_client_id
            ? { ...row, collection_enabled: previousCollectionEnabled }
            : row,
        ),
      );
      const serverMessage = error?.response?.data?.errors?.[0] || error?.response?.data?.error;
      showErrorSnackBar?.(serverMessage || 'Failed to update client status.');
    }
  };

  const submitPermanent = async (client: CollectionClient, permanentlyDisabled: boolean, reason?: string) => {
    const id = client.collections_client_id as number;

    // Optimistic UI for snappy feedback; overwrite with server audit fields once the PUT returns
    // (same pattern as CollectionClientDetailPage).
    const previous = {
      collection_permanently_disabled: client.collection_permanently_disabled,
      collection_permanently_disabled_at: client.collection_permanently_disabled_at,
      collection_permanently_disabled_reason: client.collection_permanently_disabled_reason,
      collection_permanently_disabled_by: client.collection_permanently_disabled_by,
    };
    const optimisticFields = {
      collection_permanently_disabled: permanentlyDisabled,
      collection_permanently_disabled_at: permanentlyDisabled ? new Date().toISOString() : null,
      collection_permanently_disabled_reason: permanentlyDisabled ? (reason ?? null) : null,
      collection_permanently_disabled_by: permanentlyDisabled ? (user?.name ?? user?.email ?? null) : null,
    };
    setClients((prev) => prev.map((row) => (row.collections_client_id === id ? { ...row, ...optimisticFields } : row)));

    try {
      const res = await updatePermanentStatus({ id, permanentlyDisabled, reason });
      setClients((prev) =>
        prev.map((row) => {
          if (row.collections_client_id !== id) return row;
          return {
            ...row,
            collection_permanently_disabled: res.data.collection_permanently_disabled,
            collection_permanently_disabled_at: res.data.collection_permanently_disabled_at,
            collection_permanently_disabled_reason: res.data.collection_permanently_disabled_reason,
            collection_permanently_disabled_by: res.data.collection_permanently_disabled_by,
          };
        }),
      );
      void queryClient.invalidateQueries({ queryKey: collectionCandidatesQueryKey(location) });
      showSuccessSnackBar?.(
        permanentlyDisabled
          ? `Collections permanently disabled for ${client.company}.`
          : `Collections reactivated for ${client.company}.`,
      );
    } catch (error: any) {
      setClients((prev) => prev.map((row) => (row.collections_client_id === id ? { ...row, ...previous } : row)));
      const serverMessage = error?.response?.data?.errors?.[0] || error?.response?.data?.error;
      showErrorSnackBar?.(serverMessage || 'Failed to update permanent status.');
    }
  };

  // OFF just needs a confirm; ON requires a recorded reason, so it opens the modal and the actual
  // mutation fires from the modal's onConfirm.
  const handlePermanentToggle = (client: CollectionClient, checked: boolean) => {
    if (!client.collections_client_id) return;
    if (checked) {
      setPermanentModal({ open: true, client });
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Reactivate ${client.company}? Collection emails can be turned on again after this.`)) return;
    void submitPermanent(client, false);
  };

  const handleInvoicesClick = (company: string, documents: CollectionInvoiceDocument[], acctNo: string) => {
    setInvoicesModal({ open: true, company, documents, acctNo });
  };

  const handleNotesClick = (row: CollectionClient) => {
    if (!row.collections_client_id) return;
    setNotesModal({ open: true, clientId: row.collections_client_id, company: row.company });
  };

  const handleViewClient = (row: CollectionClient) => {
    if (!row.collections_client_id) return;
    history.push(`/collections/clients/${row.collections_client_id}`);
  };

  const handleInvoicePdfUploaded = (applyTo: string, pdf: CollectionInvoicePdf, acctNoForRow: string) => {
    setInvoicesModal((prev) => ({
      ...prev,
      documents: prev.documents.map((d) => (d.apply_to === applyTo ? { ...d, has_pdf: true, pdf } : d)),
    }));
    setClients((prev) =>
      prev.map((row) => {
        if (row.acct_no !== acctNoForRow) return row;
        const nextDocs = row.invoice_documents.map((d) => (d.apply_to === applyTo ? { ...d, has_pdf: true, pdf } : d));
        const matchedPdfs = row.matched_pdfs.some((p) => p.id === pdf.id)
          ? row.matched_pdfs
          : [...row.matched_pdfs, pdf];
        return {
          ...row,
          invoice_documents: nextDocs,
          matched_pdfs: matchedPdfs,
          pdf_count: matchedPdfs.length,
        };
      }),
    );
    void queryClient.invalidateQueries({ queryKey: collectionCandidatesQueryKey(location) });
    showSuccessSnackBar?.('Invoice PDF uploaded.');
  };

  const handleInvoicePdfDeleted = (applyTo: string, pdfId: number, acctNoForRow: string) => {
    setInvoicesModal((prev) => ({
      ...prev,
      documents: prev.documents.map((d) => (d.apply_to === applyTo ? { ...d, has_pdf: false, pdf: null } : d)),
    }));
    setClients((prev) =>
      prev.map((row) => {
        if (row.acct_no !== acctNoForRow) return row;
        const nextDocs = row.invoice_documents.map((d) =>
          d.apply_to === applyTo ? { ...d, has_pdf: false, pdf: null } : d,
        );
        const matchedPdfs = row.matched_pdfs.filter((p) => p.id !== pdfId);
        return {
          ...row,
          invoice_documents: nextDocs,
          matched_pdfs: matchedPdfs,
          pdf_count: matchedPdfs.length,
        };
      }),
    );
    void queryClient.invalidateQueries({ queryKey: collectionCandidatesQueryKey(location) });
  };

  const tableData = isError ? [] : filteredClients;

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Collections — Clients" titleIcon={<BusinessIcon />} />
      </Grid>
      {isError && (
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Alert severity="error">Failed to load clients.</Alert>
        </Grid>
      )}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <CollectionClientsAgingSummary
          clients={clientsForSummary}
          pastDueThresholdDays={config?.past_due_threshold_days ?? DEFAULT_PAST_DUE_THRESHOLD_DAYS}
          friendlyThresholdDays={config?.friendly_threshold_days ?? DEFAULT_FRIENDLY_THRESHOLD_DAYS}
          secondNoticeThresholdDays={config?.second_notice_threshold_days ?? DEFAULT_SECOND_NOTICE_THRESHOLD_DAYS}
          selectedBucket={selectedBucket}
          onSelectBucket={setSelectedBucket}
          loading={summaryLoading}
        />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={2}>
            <TextField
              label="Company Name"
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              size="small"
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Bill Code"
              value={billCodeFilter}
              onChange={(e) => setBillCodeFilter(e.target.value)}
              size="small"
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="collections-filter-emailable">Emailable</InputLabel>
              <Select
                labelId="collections-filter-emailable"
                label="Emailable"
                value={emailableFilter}
                onChange={(e) => setEmailableFilter(e.target.value as TriState)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="collections-filter-collection-enabled">Collection Emails</InputLabel>
              <Select
                labelId="collections-filter-collection-enabled"
                label="Collection Emails"
                value={collectionEnabledFilter}
                onChange={(e) => setCollectionEnabledFilter(e.target.value as TriState)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <DepartmentGroupSelect
              labelId="collections-filter-department-group"
              value={departmentGroupFilter}
              onChange={setDepartmentGroupFilter}
              options={departmentGroupOptions}
              myGroups={myGroups}
              myGroupsLabel={myGroupsLabel}
            />
          </Grid>
          <Grid item xs={12} md="auto">
            <Button variant="outlined" size="small" onClick={handleClearFilters} sx={{ mb: 0.25 }}>
              Clear filters
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} sx={{ mt: 2.5 }}>
        <CollectionClientsTableTooltip />
        <Card>
          <DataTable
            columns={buildColumns(
              handleInvoicesClick,
              handleToggleClient,
              isToggling,
              handleNotesClick,
              handleViewClient,
              isCollectionsAdmin,
              handlePermanentToggle,
              isPermanentSaving,
            )}
            data={tableData}
            progressPending={tablePending}
            pagination
            paginationPerPage={25}
            paginationRowsPerPageOptions={[25, 50, 100]}
            defaultSortField="total_past_due"
            defaultSortAsc={false}
            highlightOnHover
            pointerOnHover
            onRowClicked={handleViewClient}
            striped
            noHeader
            conditionalRowStyles={[
              {
                when: (row: CollectionClient) => row.collection_permanently_disabled,
                // Override striped `:nth-child(odd)` (higher specificity than a bare backgroundColor).
                style: {
                  backgroundColor: '#ffebee',
                  '&:nth-child(odd)': { backgroundColor: '#ffebee' },
                },
              },
            ]}
          />
        </Card>
      </Grid>
      <InvoicePdfsModal
        open={invoicesModal.open}
        onClose={() => setInvoicesModal({ open: false, company: '', documents: [], acctNo: '' })}
        company={invoicesModal.company}
        documents={invoicesModal.documents}
        location={location}
        acctNo={invoicesModal.acctNo}
        onInvoicePdfUploaded={handleInvoicePdfUploaded}
        onInvoicePdfDeleted={handleInvoicePdfDeleted}
      />
      <CollectionClientNotesModal
        open={notesModal.open}
        onClose={() => setNotesModal({ open: false, clientId: 0, company: '' })}
        clientId={notesModal.clientId}
        company={notesModal.company}
        onNotesChanged={() => {
          void queryClient.invalidateQueries({ queryKey: collectionCandidatesQueryKey(location) });
        }}
      />
      <PermanentOffReasonModal
        open={permanentModal.open}
        company={permanentModal.client?.company ?? ''}
        isSubmitting={isPermanentSaving}
        onClose={() => setPermanentModal({ open: false, client: null })}
        onConfirm={(reason) => {
          const { client } = permanentModal;
          setPermanentModal({ open: false, client: null });
          if (client) void submitPermanent(client, true, reason);
        }}
      />
    </Grid>
  );
};

export default CollectionClientsListPage;
